import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useAuth } from '@/contexts/AuthContext';
import { User, LogOut, Settings, LayoutDashboard } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

export interface EnhancedHeaderProps {
  onGetStarted?: () => void;
  onLogin?: () => void;
  onLogout?: () => void;
  variant?: 'landing' | 'app';
  transparent?: boolean;
}

/**
 * Enhanced, vibrant header with modern styling and animations
 */
export const EnhancedHeader: React.FC<EnhancedHeaderProps> = ({
  onGetStarted,
  onLogin,
  onLogout,
  variant = 'landing',
  transparent = false,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { isMobile } = useBreakpoint();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle scrolling to section when landing on page with hash
  useEffect(() => {
    if (location.pathname === '/' && location.hash) {
      const sectionId = location.hash.substring(1);
      // Add small delay to ensure page is loaded
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          // Calculate offset for fixed header plus additional spacing
          const headerHeight = 80; // Account for fixed header height
          const additionalOffset = 20; // Extra spacing for better UX
          const totalOffset = headerHeight + additionalOffset;
          
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - totalOffset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [location]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isUserMenuOpen && !target.closest('.relative')) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  // Handle navigation clicks
  const handleNavClick = (href: string, e: React.MouseEvent) => {
    e.preventDefault();
    
    // If it's a hash link (like #features, #pricing, #demo)
    if (href.startsWith('#')) {
      const sectionId = href.substring(1);
      
      // If we're on the landing page, scroll to section
      if (location.pathname === '/') {
        const element = document.getElementById(sectionId);
        if (element) {
          // Calculate offset for fixed header plus additional spacing
          const headerHeight = 80; // Account for fixed header height
          const additionalOffset = 20; // Extra spacing for better UX
          const totalOffset = headerHeight + additionalOffset;
          
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - totalOffset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      } else {
        // Navigate to landing page with hash
        navigate(`/${href}`);
      }
    } else {
      // Regular navigation - always scroll to top for new pages
      navigate(href);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
    
    // Close mobile menu if open
    setIsMenuOpen(false);
  };

  const navItems = [
    { label: 'Features', href: '#features' },
    { label: 'Demo', href: '#demo' },
    { label: 'Pricing', href: '#pricing' },
    { label: 'Support', href: '/support' },
  ];

  // Handle user logout
  const handleLogout = async () => {
    try {
      await logout();
      onLogout?.();
      setIsUserMenuOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Generate user initials for avatar
  const getUserInitials = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    if (user?.first_name) {
      return user.first_name[0].toUpperCase();
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  // User menu component for authenticated users
  const renderUserMenu = () => (
    <div className="relative">
      <motion.button
        onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
        className="rounded-full hover:opacity-80 transition-opacity duration-200"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <Avatar className="w-9 h-9">
          <AvatarImage src={user?.profile_picture_url} alt={user?.first_name || user?.email} />
          <AvatarFallback className="bg-emerald-600 text-white font-semibold">
            {getUserInitials()}
          </AvatarFallback>
        </Avatar>
      </motion.button>

      {isUserMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
        >
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-xs text-gray-500 mb-1">Signed in as</p>
            <p className="text-sm text-gray-700 font-medium truncate" title={user?.email}>{user?.email}</p>
          </div>

          <motion.button
            onClick={() => {
              // Check if user has completed onboarding
              if (!user?.onboarding_completed) {
                navigate('/onboarding');
              } else {
                navigate('/dashboard');
              }
              setIsUserMenuOpen(false);
            }}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200 flex items-center space-x-2"
            whileHover={{ backgroundColor: '#f9fafb' }}
          >
            <LayoutDashboard className="w-4 h-4" />
            <span>Dashboard</span>
          </motion.button>
          
          <motion.button
            onClick={() => {
              navigate('/settings');
              setIsUserMenuOpen(false);
            }}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200 flex items-center space-x-2"
            whileHover={{ backgroundColor: '#f9fafb' }}
          >
            <Settings className="w-4 h-4" />
            <span>Settings</span>
          </motion.button>
          
          <hr className="my-1" />
          
          <motion.button
            onClick={handleLogout}
            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200 flex items-center space-x-2"
            whileHover={{ backgroundColor: '#fef2f2' }}
          >
            <LogOut className="w-4 h-4" />
            <span>Sign Out</span>
          </motion.button>
        </motion.div>
      )}
    </div>
  );

  const getHeaderClasses = () => {
    const baseClasses = 'fixed top-0 left-0 right-0 z-50 transition-all duration-300';

    if (transparent && !isScrolled) {
      return `${baseClasses} bg-transparent`;
    }
    
    if (isScrolled) {
      return `${baseClasses} bg-white/95 backdrop-blur-lg shadow-lg shadow-blue-500/10 border-b border-blue-100`;
    }
    
    return `${baseClasses} bg-white/90 backdrop-blur-sm shadow-sm`;
  };

  return (
    <motion.header
      className={getHeaderClasses()}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Enhanced Logo */}
          <motion.div
            className="flex items-center space-x-3 cursor-pointer"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2 }}
            onClick={() => {
              navigate('/');
              // Always scroll to top when logo is clicked
              setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }, 100);
            }}
          >
            <div className="relative">
              {/* Animated logo background */}
              <motion.div
                className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-cyan-600 rounded-xl flex items-center justify-center relative overflow-hidden"
                whileHover={{ rotate: 5 }}
                transition={{ duration: 0.3 }}
              >
                {/* Shimmer effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                  animate={{
                    x: [-100, 100],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
                <span className="text-white font-bold text-xl relative z-10">L</span>
              </motion.div>
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-xl bg-gradient-to-r from-emerald-600 to-cyan-600 bg-clip-text text-transparent">
                LotteryFlow
              </span>
              {variant === 'landing' && (
                <span className="text-xs text-gray-500 -mt-1">by Parallelscore</span>
              )}
            </div>
          </motion.div>

          {/* Desktop Navigation */}
          {!isMobile && (
            <nav className="hidden md:flex items-center space-x-8">
              {navItems.map((item, index) => (
                <motion.a
                  key={item.label}
                  href={item.href}
                  onClick={(e) => handleNavClick(item.href, e)}
                  className="relative group flex items-center space-x-1 px-3 py-2 rounded-lg text-gray-700 hover:text-emerald-600 transition-all duration-300 cursor-pointer"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -2 }}
                >
                  <span className="font-medium">{item.label}</span>
                  
                  {/* Hover underline effect */}
                  <motion.div
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full"
                    initial={{ scaleX: 0 }}
                    whileHover={{ scaleX: 1 }}
                    transition={{ duration: 0.3 }}
                  />
                  
                  {/* Hover glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10" />
                </motion.a>
              ))}
            </nav>
          )}

          {/* Desktop Actions */}
          {!isMobile && (
            <motion.div
              className="hidden md:flex items-center space-x-4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              {isAuthenticated && user ? (
                renderUserMenu()
              ) : (
                <>
                  <motion.button
                    onClick={onLogin}
                    className="px-4 py-2 text-gray-700 hover:text-cyan-600 font-medium transition-colors duration-200"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Sign In
                  </motion.button>
                  
                  <motion.button
                    onClick={onGetStarted}
                    className="relative px-6 py-3 bg-gradient-to-r from-emerald-500 to-cyan-600 text-white font-semibold rounded-full overflow-hidden group"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {/* Button shimmer effect */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                      animate={{
                        x: [-100, 100],
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    />
                    <span className="relative z-10 flex items-center space-x-2">
                      <span>Get Started</span>
                      <motion.span
                        animate={{ x: [0, 3, 0] }}
                        transition={{ duration: 1, repeat: Infinity }}
                      >
                        →
                      </motion.span>
                    </span>
                  </motion.button>
                </>
              )}
            </motion.div>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <motion.button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="relative p-3 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-200/50"
              whileTap={{ scale: 0.95 }}
            >
              <div className="w-6 h-6 flex flex-col justify-center items-center space-y-1">
                <motion.div
                  className="w-5 h-0.5 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full"
                  animate={{
                    rotate: isMenuOpen ? 45 : 0,
                    y: isMenuOpen ? 6 : 0,
                  }}
                  transition={{ duration: 0.3 }}
                />
                <motion.div
                  className="w-5 h-0.5 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full"
                  animate={{
                    opacity: isMenuOpen ? 0 : 1,
                  }}
                  transition={{ duration: 0.3 }}
                />
                <motion.div
                  className="w-5 h-0.5 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full"
                  animate={{
                    rotate: isMenuOpen ? -45 : 0,
                    y: isMenuOpen ? -6 : 0,
                  }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </motion.button>
          )}
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobile && (
        <>
          {/* Backdrop */}
          <motion.div
            className="md:hidden fixed inset-0 bg-black/50 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: isMenuOpen ? 1 : 0 }}
            transition={{ duration: 0.3 }}
            style={{ 
              pointerEvents: isMenuOpen ? 'auto' : 'none',
            }}
            onClick={() => setIsMenuOpen(false)}
          />

          {/* Sliding Menu Panel */}
          <motion.div
            className="md:hidden fixed top-0 right-0 h-full w-80 bg-white/95 backdrop-blur-lg shadow-xl z-50"
            initial={{ x: '100%' }}
            animate={{ x: isMenuOpen ? 0 : '100%' }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <div className="p-6 space-y-6">
              {/* Menu Header */}
              <div className="flex items-center justify-between">
                <span className="text-lg font-semibold text-gray-900">
                  Menu
                </span>
                <motion.button
                  onClick={() => setIsMenuOpen(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  whileTap={{ scale: 0.95 }}
                >
                  <svg
                    className="w-6 h-6 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </motion.button>
              </div>

              {/* Mobile Navigation Links */}
              {navItems.map((item, index) => (
                <motion.a
                  key={item.label}
                  href={item.href}
                  onClick={(e) => handleNavClick(item.href, e)}
                  className="flex items-center space-x-3 p-3 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-all duration-300 cursor-pointer"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: isMenuOpen ? 1 : 0, x: isMenuOpen ? 0 : 20 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <span className="font-medium text-gray-700">{item.label}</span>
                </motion.a>
              ))}

              {/* Mobile Actions */}
              <div className="pt-4 border-t border-gray-200 space-y-3">
                {isAuthenticated && user ? (
                  <>
                    {/* User info section */}
                    <div className="px-3 py-3 bg-gray-50 rounded-xl">
                      <p className="text-sm font-medium text-gray-700 truncate">
                        {user?.first_name && user?.last_name 
                          ? `${user.first_name} ${user.last_name}`.trim()
                          : user?.first_name || user?.last_name || 'User'
                        }
                      </p>
                      <p className="text-xs text-gray-500 truncate" title={user?.email}>{user?.email}</p>
                    </div>

                    <motion.button
                      onClick={() => {
                        // Check if user has completed onboarding
                        if (!user?.onboarding_completed) {
                          navigate('/onboarding');
                        } else {
                          navigate('/dashboard');
                        }
                        setIsMenuOpen(false);
                      }}
                      className="w-full p-3 text-left font-medium text-gray-700 hover:bg-gray-50 rounded-xl transition-colors duration-200 flex items-center space-x-2"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: isMenuOpen ? 1 : 0, y: isMenuOpen ? 0 : 20 }}
                      transition={{ duration: 0.3, delay: 0.4 }}
                    >
                      <LayoutDashboard className="w-4 h-4" />
                      <span>Dashboard</span>
                    </motion.button>

                    <motion.button
                      onClick={() => {
                        navigate('/settings');
                        setIsMenuOpen(false);
                      }}
                      className="w-full p-3 text-left font-medium text-gray-700 hover:bg-gray-50 rounded-xl transition-colors duration-200 flex items-center space-x-2"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: isMenuOpen ? 1 : 0, y: isMenuOpen ? 0 : 20 }}
                      transition={{ duration: 0.3, delay: 0.45 }}
                    >
                      <Settings className="w-4 h-4" />
                      <span>Settings</span>
                    </motion.button>

                    <motion.button
                      onClick={() => {
                        handleLogout();
                        setIsMenuOpen(false);
                      }}
                      className="w-full p-3 text-left font-medium text-red-600 hover:bg-red-50 rounded-xl transition-colors duration-200 flex items-center space-x-2"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: isMenuOpen ? 1 : 0, y: isMenuOpen ? 0 : 20 }}
                      transition={{ duration: 0.3, delay: 0.5 }}
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Sign Out</span>
                    </motion.button>
                  </>
                ) : (
                  <>
                    <motion.button
                      onClick={() => {
                        onLogin?.();
                        setIsMenuOpen(false);
                      }}
                      className="w-full p-3 text-left font-medium text-gray-700 hover:bg-gray-50 rounded-xl transition-colors duration-200"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: isMenuOpen ? 1 : 0, y: isMenuOpen ? 0 : 20 }}
                      transition={{ duration: 0.3, delay: 0.4 }}
                    >
                      Sign In
                    </motion.button>

                    <motion.button
                      onClick={() => {
                        onGetStarted?.();
                        setIsMenuOpen(false);
                      }}
                      className="w-full p-4 bg-gradient-to-r from-emerald-500 to-cyan-600 text-white font-semibold rounded-xl"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: isMenuOpen ? 1 : 0, y: isMenuOpen ? 0 : 20 }}
                      transition={{ duration: 0.3, delay: 0.5 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Get Started Free
                    </motion.button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </motion.header>
  );
};
